import React, { useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { T } from './ui';

/**
 * The FanCam challenge a campaign's button points at, chosen by sight.
 *
 * This was a native select of captions, and many challenges have none, so an operator faced a list
 * of "Untitled challenge" with no way to tell them apart. Each row now shows the challenge's own
 * thumbnail beside its caption, team and date, with a search box at the top of the list.
 *
 * A button and a listbox rather than a select, because a select cannot hold an image.
 */

/**
 * Whether a challenge matches what an operator typed: every word, in any order, in the caption or
 * the team, so "tokyo final" finds "Cup Final FanCam" for FC Tokyo.
 */
export const challengeMatches = (challenge, query) => {
  const words = (query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;

  const haystack = `${challenge.caption || ''} ${challenge.teamName || ''}`.toLowerCase();
  return words.every((word) => haystack.includes(word));
};

const UNTITLED = 'Untitled challenge';

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    : null;

/** Team, date and media type: what tells two untitled challenges apart after the picture. */
const detailsOf = (challenge) =>
  [
    challenge.teamName,
    formatDate(challenge.createdAt),
    challenge.mediaType === 'video' ? 'Video' : challenge.mediaType === 'image' ? 'Photo' : null
  ]
    .filter(Boolean)
    .join(' · ');

const Wrap = styled.div`
  position: relative;
`;

const Trigger = styled.button`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 46px;
  padding: 6px 34px 6px 8px;
  font-family: inherit;
  font-size: 14px;
  text-align: left;
  color: ${T.heading};
  background: ${T.card}
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")
    no-repeat right 11px center;
  border: 1px solid ${T.border};
  border-radius: 9px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${T.primary};
    box-shadow: 0 0 0 3px ${T.primarySoft};
  }

  &:disabled {
    background-color: ${T.panel};
    cursor: not-allowed;
  }
`;

const Panel = styled.div`
  position: absolute;
  z-index: 30;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: ${T.card};
  border: 1px solid ${T.border};
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
  overflow: hidden;
`;

const Search = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 0;
  border-bottom: 1px solid ${T.rowBorder};
  padding: 12px 14px;
  font-family: inherit;
  font-size: 14px;
  color: ${T.heading};

  &::placeholder {
    color: ${T.faint};
  }

  &:focus {
    outline: none;
    background: ${T.panel};
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 4px;
  max-height: 340px;
  overflow-y: auto;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  background: ${(props) => (props.$active ? T.primarySoft : 'transparent')};
`;

const Text = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Caption = styled.span`
  font-weight: 600;
  color: ${(props) => (props.$untitled ? T.muted : T.heading)};
  font-style: ${(props) => (props.$untitled ? 'italic' : 'normal')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Details = styled.span`
  font-size: 12px;
  color: ${T.faint};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Warning = styled.span`
  font-size: 12px;
  color: ${T.amber};
`;

const Footer = styled.div`
  padding: 8px 14px;
  border-top: 1px solid ${T.rowBorder};
  font-size: 12px;
  color: ${T.faint};
`;

const ThumbBox = styled.span`
  flex: none;
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  border-radius: 7px;
  overflow: hidden;
  background: ${T.slateSoft};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: ${T.slate};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

/** The challenge's picture, or a labelled tile when it has none or it fails to load. */
const Thumb = ({ challenge, size }) => {
  const [failed, setFailed] = useState(false);
  const src = challenge?.thumbnailUrl;

  return (
    <ThumbBox $size={size} aria-hidden="true">
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
      ) : challenge?.mediaType === 'video' ? (
        'Video'
      ) : (
        'No image'
      )}
    </ThumbBox>
  );
};

const ChallengePicker = ({ id, challenges = [], value, onChange, noteFor, disabled, ...aria }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const listId = useId();

  const chosen = challenges.find((challenge) => challenge.id === value);
  const shown = challenges.filter((challenge) => challengeMatches(challenge, query));

  // Closes on a click anywhere else, as a select would.
  useEffect(() => {
    if (!open) return undefined;

    const close = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const openPanel = () => {
    setQuery('');
    setActive(Math.max(0, challenges.indexOf(chosen)));
    setOpen(true);
  };

  const choose = (challenge) => {
    onChange(challenge.id);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => Math.min(index + 1, shown.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && shown[active]) {
      event.preventDefault();
      choose(shown[active]);
    }
  };

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => (open ? setOpen(false) : openPanel())}
        {...aria}
      >
        {chosen ? (
          <>
            <Thumb challenge={chosen} size={34} />
            <Text>
              <Caption $untitled={!chosen.caption || chosen.caption === UNTITLED}>
                {chosen.caption || UNTITLED}
              </Caption>
              <Details>{detailsOf(chosen)}</Details>
            </Text>
          </>
        ) : (
          <span style={{ color: T.faint, padding: '6px 5px' }}>Choose a challenge</span>
        )}
      </Trigger>

      {open ? (
        <Panel onKeyDown={onKeyDown}>
          <Search
            // eslint-disable-next-line jsx-a11y/no-autofocus -- the panel opens to be searched
            autoFocus
            type="search"
            aria-label="Find a challenge"
            aria-controls={listId}
            placeholder="Search by caption or team"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
          />

          <List id={listId} role="listbox" aria-label="FanCam challenges">
            {shown.map((challenge, index) => {
              const note = noteFor?.(challenge);
              const untitled = !challenge.caption || challenge.caption === UNTITLED;

              return (
                <Row
                  key={challenge.id}
                  role="option"
                  aria-selected={challenge.id === value}
                  $active={index === active}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => choose(challenge)}
                >
                  <Thumb challenge={challenge} size={44} />
                  <Text>
                    <Caption $untitled={untitled}>{challenge.caption || UNTITLED}</Caption>
                    <Details>{detailsOf(challenge)}</Details>
                    {note ? <Warning>{note}</Warning> : null}
                  </Text>
                </Row>
              );
            })}
          </List>

          <Footer role="status">
            {!shown.length
              ? 'No challenge matches your search.'
              : query
                ? `${shown.length} of ${challenges.length} challenges match.`
                : `${challenges.length} challenge${challenges.length === 1 ? '' : 's'}, newest first.`}
          </Footer>
        </Panel>
      ) : null}
    </Wrap>
  );
};

export default ChallengePicker;
